import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Copy, Trash2 } from 'lucide-react';
import DictationService, {
  DictationItem,
} from '../../services/dictationService';

interface DictationHistoryProps {
  onCopy?: (text: string) => void;
}

const DictationHistory: React.FC<DictationHistoryProps> = ({ onCopy }) => {
  const { t } = useTranslation();
  const [dictations, setDictations] = useState<DictationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDictations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await DictationService.getDictations();
      setDictations(data);
    } catch (err) {
      console.error('Failed to load dictations:', err);
      setError(
        t(
          'dictation.history.errors.failedToLoad',
          'Failed to load dictation history'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  // Load dictations on mount
  useEffect(() => {
    loadDictations();
  }, []);

  const handleCopy = async (text: string) => {
    try {
      await DictationService.copyToClipboard(text);
      if (onCopy) {
        onCopy(text);
      }
    } catch (err) {
      console.error('Failed to copy dictation:', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await DictationService.deleteDictation(id);
      setDictations((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      console.error('Failed to delete dictation:', err);
      setError(
        t(
          'dictation.history.errors.failedToDelete',
          'Failed to delete dictation'
        )
      );
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (dictations.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {t('dictation.history.empty.title', 'No dictations yet')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(
            'dictation.history.empty.description',
            'Start recording to see your dictation history here'
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <List sx={{ maxHeight: 400, overflow: 'auto', pt: 0 }}>
        {dictations.map((dictation, index) => (
          <React.Fragment key={dictation.id}>
            <ListItem
              sx={{
                p: 2,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&:hover .action-buttons': {
                  opacity: 1,
                },
              }}
            >
              <ListItemText
                primary={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.4,
                        flex: 1,
                        mr: 2,
                      }}
                    >
                      {dictation.text}
                    </Typography>
                    <Box
                      className="action-buttons"
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                        opacity: 0,
                        transition: 'opacity 0.2s',
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(dictation.text)}
                        title={t(
                          'dictation.history.actions.copyToClipboard',
                          'Copy to clipboard'
                        )}
                        sx={{ color: 'primary.main' }}
                      >
                        <Copy size={16} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(dictation.id)}
                        title={t(
                          'dictation.history.actions.deleteDictation',
                          'Delete dictation'
                        )}
                        sx={{ color: 'error.main' }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Box>
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(dictation.createdAt)}
                  </Typography>
                }
              />
            </ListItem>
            {index < dictations.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};

export default DictationHistory;
